// API configuration
import axios from 'axios';
import Cookies from 'js-cookie';
import { DEBUG, BASE_DOMAIN, BASE_URL } from '../conf';

// Create a separate instance for session management
const sessionAPI = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    'X-Requested-With': 'XMLHttpRequest'
  }
});

// Add CSRF token to session API if available
sessionAPI.interceptors.request.use(config => {
  const csrfToken = Cookies.get('csrftoken');
  if (csrfToken) {
    config.headers['X-CSRFToken'] = csrfToken;
  }
  const sessionId = getStoredSession();
  if (sessionId) {
    config.headers['X-Session-Id'] = sessionId;
  }
  return config;
});

// State variables for session management
let currentSessionId = null;      // Caches the current session ID
let isSessionFetching = false;    // Prevents multiple concurrent session fetches
const requestQueue = [];          // Queue for requests waiting for session
const SHOP_SESSION_STORAGE_KEY = 'relightsa_shop_session_id';

const getShopSessionFromStorage = () => {
  try {
    return localStorage.getItem(SHOP_SESSION_STORAGE_KEY);
  } catch (error) {
    return null;
  }
};

const setShopSessionInStorage = (sessionId) => {
  try {
    if (sessionId) {
      localStorage.setItem(SHOP_SESSION_STORAGE_KEY, sessionId);
    }
  } catch (error) {
    console.error('Unable to persist shop session:', error);
  }
};

const clearShopSessionFromStorage = () => {
  try {
    localStorage.removeItem(SHOP_SESSION_STORAGE_KEY);
  } catch (error) {
    console.error('Unable to clear shop session:', error);
  }
};

const adoptSessionFromResponse = (response) => {
  const responseSessionId = response?.headers?.['x-session-id'];
  if (responseSessionId) {
    currentSessionId = responseSessionId;
    setShopSessionInStorage(responseSessionId);
  }
  return response;
};

/**
 * Gets the session ID from cookies or cache
 */
const getStoredSession = () => {
  // First check our cache
  if (currentSessionId) {
    return currentSessionId;
  }
  
  // Keep storefront basket sessions separate from Django admin/auth cookies.
  const sessionId = getShopSessionFromStorage();
  if (sessionId) {
    currentSessionId = sessionId;
    return sessionId;
  }
  
  return null;
};

/**
 * Validates or creates a session
 * 
 * @returns {Promise<string>} A promise that resolves to the session ID
 */
const getValidSession = async () => {
  // First check for stored session
  const storedSession = getStoredSession();
  
  if (storedSession) {
    try {
      // Validate existing session
      const response = await sessionAPI.head('/api/session/', {
        headers: {
          'X-Session-Id': storedSession
        }
      });
      if (response.status === 200) {
        return storedSession;
      }
    } catch (error) {
      console.log('Session validation failed, will create new one');
      currentSessionId = null;
      clearShopSessionFromStorage();
    }
  }

  // If we're already fetching a session, queue this request
  if (isSessionFetching) {
    return new Promise((resolve) => {
      requestQueue.push({ resolve });
    });
  }

  try {
    isSessionFetching = true;
    
    // Create new session with single POST request
    const response = await sessionAPI.post('/api/session/');
    
    // Store both session ID and CSRF token
    currentSessionId = response.data.session_id;
    setShopSessionInStorage(currentSessionId);
    if (response.data.csrf_token) {
      Cookies.set('csrftoken', response.data.csrf_token, {
        path: '/',
        domain: DEBUG ? undefined : BASE_DOMAIN,
        sameSite: 'lax',
        secure: !DEBUG
      });
    }
    
    return currentSessionId;
    
  } catch (error) {
    console.error('Session error:', error);
    throw error;
  } finally {
    isSessionFetching = false;
    while (requestQueue.length > 0) {
      const { resolve } = requestQueue.shift();
      resolve(currentSessionId);
    }
  }
};

/**
 * Creates the main API instance with interceptors
 * 
 * This function creates an axios instance configured with:
 * 1. Base URL and credentials
 * 2. CSRF token handling
 * 3. Session management
 * 4. Error handling and retries
 * 
 * @returns {AxiosInstance} Configured axios instance
 */
const createAPI = () => {
  const instance = axios.create({
    baseURL: BASE_URL,
    withCredentials: true,
    headers: {
      'X-Requested-With': 'XMLHttpRequest'
    }
  });

  // Request interceptor for session management
  instance.interceptors.request.use(async (config) => {
    // Skip session check for session endpoints
    if (config.url.includes('/api/session/')) {
      const csrfToken = Cookies.get('csrftoken');
      const sessionId = getStoredSession();
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      }
      if (sessionId) {
        config.headers['X-Session-Id'] = sessionId;
      }
      return config;
    }

    try {
      // Get current session or create new one
      const sessionId = await getValidSession();
      
      // Add headers
      const csrfToken = Cookies.get('csrftoken');
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      }
      if (sessionId) {
        config.headers['X-Session-Id'] = sessionId;
      }
      
      return config;
    } catch (error) {
      console.error('Session management failed:', error);
      return Promise.reject(new Error('No valid session available'));
    }
  });

  // Response interceptor for session errors
  instance.interceptors.response.use(
    response => adoptSessionFromResponse(response),
    async error => {
      const isAuthEndpoint = error.config.url.includes('/api/token/') || error.config.url.includes('/api/session/');
      const isCSRFError = error.response?.data?.detail?.includes('CSRF');
      
      // Add rate limiting for session retries
      if (isCSRFError || ((error.response?.status === 401 || error.response?.status === 403) && !isAuthEndpoint)) {
        if (!error.config.__isRetry) {
          error.config.__isRetry = true;
          try {
            // Clear current session and CSRF token
            currentSessionId = null;
            clearShopSessionFromStorage();
            Cookies.remove('csrftoken');
            
            // Get new session and CSRF token
            const sessionId = await getValidSession();
            if (sessionId) {
              // Update the request with new session and CSRF token
              error.config.headers['X-Session-Id'] = sessionId;
              const csrfToken = Cookies.get('csrftoken');
              if (csrfToken) {
                error.config.headers['X-CSRFToken'] = csrfToken;
              }
              return instance(error.config);
            }
          } catch (retryError) {
            console.error('Failed to get new session:', retryError);
          }
        }
      }
      return Promise.reject(error);
    }
  );
  
  return instance;
};

// Create and configure the API instance
const api = createAPI();

// Configure session API error handling
sessionAPI.interceptors.response.use(
  response => adoptSessionFromResponse(response),
  error => {
    console.error('Session API error:', error);
    return Promise.reject(error);
  }
);

export const clearSessionStorage = () => {
  console.log('Clearing all session storage');
  try {
    const cookieOptions = {
      path: '/',
      domain: DEBUG ? undefined : BASE_DOMAIN,
      sameSite: 'lax',
      secure: !DEBUG
    };

    Cookies.remove('sessionid', cookieOptions);
    clearShopSessionFromStorage();
    
    currentSessionId = null;
    console.log('All session storage cleared');
  } catch (error) {
    console.error('Error clearing session storage:', error);
  }
};

export { api };
export default api;
